import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import GoToDashboardButton from './GoToDashboardButton';
import { sendAuthPageVisit } from '../api/auth';
import { fetchSavedReports, saveBuiltReport } from '../api/ops';
import styles from './ReportBuilderPage.module.css';
import SerialPassFailChart from './SerialPassFailChart';
import ParetoChart from './ParetoChart';
import UUTStatusSummaryChart from './UUTStatusSummaryChart';
import ControlChart from './ControlChart';
import RootCausePrediction from './RootCausePrediction';
import StepFrequencyChart from './StepFrequencyChart';
import NormalDistributionChart from './NormalDistributionChart';
import PredictiveFailureAnalysis from './PredictiveFailureAnalysis';
import RootCauseAlarms from './RootCauseAlarms';
import FirstFailFingerprint from './FirstFailFingerprint';
import LimitMarginRisk from './LimitMarginRisk';
import ProductionHotspots from './ProductionHotspots';
import CascadeFailureSpread from './CascadeFailureSpread';
import RetryPressure from './RetryPressure';
import LateFailureCost from './LateFailureCost';
import NewControlChart from './NewControlChart';
import MlRunDetail from '../pages/MlRunDetail';
import {
  BurnInCategoricalValuesPage,
  BurnInControlChartPage,
  BurnInDataCompletenessPage,
  BurnInFailureParetoPage,
  BurnInHourlyActivityPage,
  BurnInLimitCompliancePage,
  BurnInNearLimitRiskPage,
  BurnInParetoChartPage,
  BurnInPieChartPage,
  BurnInRootCauseAnalyzerPage,
  BurnInSequenceBandsPage,
  BurnInSequenceHotspotsPage,
  BurnInSourceHealthPage,
  BurnInStatusPage,
  BurnInStepFailRatePage,
  BurnInStepVolumePage,
  BurnInTemperatureAnalyzerPage,
  BurnInTimelinePage,
  BurnInUUTStatusSummaryPage,
  BurnInUploadComparisonPage,
  BurnInUnitsPage,
  BurnInValueSpreadPage,
} from './burnIn/BurnInAnalyticsPages';
import { artavaBrand } from '../branding/artavaBrand';
import { applyBrandedPdfChrome } from '../branding/exportBranding';

Modal.setAppElement('#root');
const DEFAULT_CAPTURE_SELECTORS = [
  '[data-report-capture-root="true"]',
  '.chartBox',
  '.chartSection',
  '.chartContainer',
  '.chart-card',
  '.widgetCard',
  '.panelCard',
  'canvas',
  'svg',
];

const REPORT_DOMAINS = [
  { key: 'production', label: 'Test machine' },
  { key: 'burn-in', label: 'Burn-in' },
];

const PRODUCTION_CHARTS = [
  { key: 'production-pie', label: 'Pass / fail pie chart', description: 'Real production pass/fail chart with its real filters.', Component: SerialPassFailChart },
  { key: 'production-pareto', label: 'Pareto chart', description: 'Real production pareto chart with its real filters.', Component: ParetoChart },
  { key: 'production-uut-summary', label: 'UUT status summary', description: 'Real production UUT summary page.', Component: UUTStatusSummaryChart },
  { key: 'production-control-chart', label: 'Control chart', description: 'Real production control chart page.', Component: ControlChart, componentProps: { embedded: true }, captureSelector: '.chartContainer' },
  { key: 'production-new-control-chart', label: 'New control chart', description: 'Real new control chart page using the most recent calculator result.', Component: NewControlChart, componentProps: { embedded: true } },
  { key: 'production-root-cause', label: 'Root cause analyzer', description: 'Real production root cause page.', Component: RootCausePrediction },
  { key: 'production-step-frequency', label: 'Step frequency analyzer', description: 'Real step frequency page.', Component: StepFrequencyChart },
  { key: 'production-normal-distribution', label: 'Normal distribution', description: 'Real normal distribution page.', Component: NormalDistributionChart },
  { key: 'production-predictive-analysis', label: 'Predictive analysis', description: 'Real predictive failure page.', Component: PredictiveFailureAnalysis },
  { key: 'production-root-cause-alarms', label: 'Root cause alarms', description: 'Real alarms page.', Component: RootCauseAlarms },
  { key: 'production-first-fail', label: 'First-fail fingerprint', description: 'Real first-fail fingerprint page.', Component: FirstFailFingerprint },
  { key: 'production-limit-margin', label: 'Limit margin risk', description: 'Real limit-margin page.', Component: LimitMarginRisk },
  { key: 'production-hotspots', label: 'Production hotspots', description: 'Real hotspot page.', Component: ProductionHotspots },
  { key: 'production-cascade', label: 'Cascade failure spread', description: 'Real cascade spread page.', Component: CascadeFailureSpread },
  { key: 'production-retry-pressure', label: 'Retry pressure', description: 'Real retry-pressure page.', Component: RetryPressure },
  { key: 'production-late-failure', label: 'Late-stage failure cost', description: 'Real late-failure-cost page.', Component: LateFailureCost },
  { key: 'production-ml-runs', label: 'ML runs', description: 'Real production ML runs page.', Component: MlRunDetail },
];

const BURN_IN_CHARTS = [
  { key: 'burnin-temperature-analyzer', label: 'Temperature analyzer', description: 'Real burn-in temperature analyzer page.', Component: BurnInTemperatureAnalyzerPage },
  { key: 'burnin-pie-chart', label: 'Pie chart analysis', description: 'Real burn-in pie chart page.', Component: BurnInPieChartPage },
  { key: 'burnin-pareto-chart', label: 'Pareto chart analysis', description: 'Real burn-in pareto chart page.', Component: BurnInParetoChartPage },
  { key: 'burnin-control-chart', label: 'Control chart analysis', description: 'Real burn-in control chart page.', Component: BurnInControlChartPage },
  { key: 'burnin-uut-summary', label: 'UUT status summary', description: 'Real burn-in UUT summary page.', Component: BurnInUUTStatusSummaryPage },
  { key: 'burnin-root-cause', label: 'Root cause analyzer', description: 'Real burn-in root cause page.', Component: BurnInRootCauseAnalyzerPage },
  { key: 'burnin-status', label: 'Status overview', description: 'Real burn-in status page.', Component: BurnInStatusPage },
  { key: 'burnin-failure-pareto', label: 'Failure pareto', description: 'Real burn-in failure pareto page.', Component: BurnInFailureParetoPage },
  { key: 'burnin-step-fail-rate', label: 'Step fail rate', description: 'Real burn-in step fail-rate page.', Component: BurnInStepFailRatePage },
  { key: 'burnin-step-volume', label: 'Step volume', description: 'Real burn-in step-volume page.', Component: BurnInStepVolumePage },
  { key: 'burnin-timeline', label: 'Timeline', description: 'Real burn-in timeline page.', Component: BurnInTimelinePage },
  { key: 'burnin-hourly-activity', label: 'Hourly activity', description: 'Real burn-in hourly-activity page.', Component: BurnInHourlyActivityPage },
  { key: 'burnin-source-health', label: 'Source health', description: 'Real burn-in source-health page.', Component: BurnInSourceHealthPage },
  { key: 'burnin-upload-comparison', label: 'Upload comparison', description: 'Real burn-in upload-comparison page.', Component: BurnInUploadComparisonPage },
  { key: 'burnin-units', label: 'Units breakdown', description: 'Real burn-in units page.', Component: BurnInUnitsPage },
  { key: 'burnin-data-completeness', label: 'Data completeness', description: 'Real burn-in completeness page.', Component: BurnInDataCompletenessPage },
  { key: 'burnin-sequence-hotspots', label: 'Sequence hotspots', description: 'Real burn-in sequence-hotspots page.', Component: BurnInSequenceHotspotsPage },
  { key: 'burnin-sequence-bands', label: 'Sequence bands', description: 'Real burn-in sequence-bands page.', Component: BurnInSequenceBandsPage },
  { key: 'burnin-value-spread', label: 'Value spread', description: 'Real burn-in value-spread page.', Component: BurnInValueSpreadPage },
  { key: 'burnin-limit-compliance', label: 'Limit compliance', description: 'Real burn-in limit-compliance page.', Component: BurnInLimitCompliancePage },
  { key: 'burnin-near-limit-risk', label: 'Near-limit risk', description: 'Real burn-in near-limit-risk page.', Component: BurnInNearLimitRiskPage },
  { key: 'burnin-categorical-values', label: 'Categorical values', description: 'Real burn-in categorical-values page.', Component: BurnInCategoricalValuesPage },
];

const defaultHeader = {
  title: 'Smarti report',
  subtitle: 'Operational analytics summary',
  customerName: '',
  preparedBy: '',
  reportDate: new Date().toISOString().slice(0, 10),
  headerNote: '',
  logoDataUrl: '',
};

const emptyCommentState = { chartKey: '', text: '' };

function sanitizeFilePart(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

function buildReportFileName({ domain, title, date, extension }) {
  const titlePart = sanitizeFilePart(title) || 'report';
  const datePart = sanitizeFilePart(date) || new Date().toISOString().slice(0, 10);
  return `${domain}-${titlePart}-${datePart}.${extension}`;
}

function formatSavedReportDate(value) {
  if (!value) return 'Just now';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Just now';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

function formatMimeLabel(mimeType) {
  return mimeType === DOCX_MIME_TYPE ? 'DOCX' : 'PDF';
}

function getSavedReportDisplayName(report) {
  const title = String(report?.title || '').trim();
  const fileName = String(report?.fileName || '').trim();
  if (title && title.toLowerCase() !== 'smarti report') return title;
  if (fileName) return fileName.replace(/\.(pdf|docx)$/i, '');
  return title || 'Smarti report';
}

function buildExecutiveSummary({ domain, header, selectedCharts, chartFindings, chartComments, chartSnapshots }) {
  const capturedCharts = selectedCharts.filter((chart) => chartSnapshots[chart.key]?.imageDataUrl);
  const findings = capturedCharts
    .map((chart) => chartFindings[chart.key]?.trim())
    .filter(Boolean)
    .slice(0, 3);
  const comments = capturedCharts
    .map((chart) => chartComments[chart.key]?.trim())
    .filter(Boolean)
    .slice(0, 2);
  const intro = `${header.customerName || 'This team'} ${domain === 'production' ? 'production' : 'burn-in'} review includes ${capturedCharts.length || selectedCharts.length} chart${capturedCharts.length === 1 || selectedCharts.length === 1 ? '' : 's'} from Smarti.`;
  const findingText = findings.length
    ? `Key findings: ${findings.join(' ')}`
    : 'Key findings will appear here once you add a finding to each captured chart.';
  const actionText = comments.length ? `Recommended follow-up: ${comments[0]}` : '';
  return [intro, findingText, actionText].filter(Boolean).join(' ');
}

function getChartCatalog(domain) {
  return domain === 'burn-in' ? BURN_IN_CHARTS : PRODUCTION_CHARTS;
}

function getReportBuilderClientMeta(path) {
  const nav = window.navigator || {};
  const screenInfo = window.screen || {};

  return {
    language: nav.language || '',
    languages: Array.isArray(nav.languages) ? nav.languages.join(', ') : '',
    platform: nav.platform || '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    screen: `${screenInfo.width || 0}x${screenInfo.height || 0}`,
    colorDepth: screenInfo.colorDepth || null,
    path,
    referrer: document.referrer || '',
  };
}

function buildReportChartVisitPath(domain, chartDefinition) {
  const params = new URLSearchParams({
    domain,
    chart: chartDefinition?.key || 'unknown',
    label: chartDefinition?.label || 'unknown',
    mode: 'modal',
  });
  return `/report-builder/chart-preview?${params.toString()}`;
}

function looksLikeChartContainer(node) {
  const className = typeof node?.className === 'string' ? node.className : '';
  return /(chart|plot|card|panel|widget|section|canvas)/i.test(className);
}

function resolveCaptureNode(root, chartDefinition) {
  if (!root) return null;
  const selectors = [
    chartDefinition?.captureSelector,
    ...DEFAULT_CAPTURE_SELECTORS,
  ].filter(Boolean);

  for (const selector of selectors) {
    const match = root.querySelector(selector);
    if (!match) continue;
    if (match === root) return root;
    if (selector === 'canvas' || selector === 'svg') {
      let current = match.parentElement;
      while (current && current !== root) {
        const rect = current.getBoundingClientRect();
        if (looksLikeChartContainer(current) && rect.width > 240 && rect.height > 180) {
          return current;
        }
        current = current.parentElement;
      }
    } else {
      return match;
    }
  }

  return root;
}

async function renderNodeToCanvas(node, scale = 2) {
  return html2canvas(node, {
    backgroundColor: '#ffffff',
    scale,
    useCORS: true,
    scrollY: -window.scrollY,
  });
}

const MESSAGING_REPORT_DATA_URL_LIMIT = 13_500_000;
const DOCX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const MESSAGING_REPORT_PDF_PROFILES = [
  { headerScale: 1.65, chartScale: 1.3, imageFormat: 'JPEG', imageMime: 'image/jpeg', imageQuality: 0.84 },
  { headerScale: 1.35, chartScale: 1.05, imageFormat: 'JPEG', imageMime: 'image/jpeg', imageQuality: 0.74 },
  { headerScale: 1.1, chartScale: 0.9, imageFormat: 'JPEG', imageMime: 'image/jpeg', imageQuality: 0.64 },
  { headerScale: 0.9, chartScale: 0.72, imageFormat: 'JPEG', imageMime: 'image/jpeg', imageQuality: 0.56 },
];

function escapeXml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function dataUrlToBytes(dataUrl) {
  const encoded = String(dataUrl || '').split(',')[1] || '';
  const binary = window.atob(encoded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

function textToBytes(value) {
  return new TextEncoder().encode(String(value || ''));
}

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let index = 0; index < 256; index += 1) {
    let current = index;
    for (let bit = 0; bit < 8; bit += 1) {
      current = current & 1 ? 0xedb88320 ^ (current >>> 1) : current >>> 1;
    }
    table[index] = current >>> 0;
  }
  return table;
})();

function crc32(bytes) {
  let crc = 0xffffffff;
  for (let index = 0; index < bytes.length; index += 1) {
    crc = CRC_TABLE[(crc ^ bytes[index]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function writeUint16(target, offset, value) {
  target[offset] = value & 0xff;
  target[offset + 1] = (value >>> 8) & 0xff;
}

function writeUint32(target, offset, value) {
  target[offset] = value & 0xff;
  target[offset + 1] = (value >>> 8) & 0xff;
  target[offset + 2] = (value >>> 16) & 0xff;
  target[offset + 3] = (value >>> 24) & 0xff;
}

function concatBytes(chunks) {
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const output = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    output.set(chunk, offset);
    offset += chunk.length;
  }
  return output;
}

function createZip(files) {
  const localChunks = [];
  const centralChunks = [];
  let offset = 0;

  for (const file of files) {
    const nameBytes = textToBytes(file.name);
    const dataBytes = file.data instanceof Uint8Array ? file.data : textToBytes(file.data);
    const checksum = crc32(dataBytes);
    const localHeader = new Uint8Array(30 + nameBytes.length);
    writeUint32(localHeader, 0, 0x04034b50);
    writeUint16(localHeader, 4, 20);
    writeUint16(localHeader, 6, 0);
    writeUint16(localHeader, 8, 0);
    writeUint32(localHeader, 14, checksum);
    writeUint32(localHeader, 18, dataBytes.length);
    writeUint32(localHeader, 22, dataBytes.length);
    writeUint16(localHeader, 26, nameBytes.length);
    localHeader.set(nameBytes, 30);
    localChunks.push(localHeader, dataBytes);

    const centralHeader = new Uint8Array(46 + nameBytes.length);
    writeUint32(centralHeader, 0, 0x02014b50);
    writeUint16(centralHeader, 4, 20);
    writeUint16(centralHeader, 6, 20);
    writeUint16(centralHeader, 8, 0);
    writeUint16(centralHeader, 10, 0);
    writeUint32(centralHeader, 16, checksum);
    writeUint32(centralHeader, 20, dataBytes.length);
    writeUint32(centralHeader, 24, dataBytes.length);
    writeUint16(centralHeader, 28, nameBytes.length);
    writeUint32(centralHeader, 42, offset);
    centralHeader.set(nameBytes, 46);
    centralChunks.push(centralHeader);
    offset += localHeader.length + dataBytes.length;
  }

  const centralDirectory = concatBytes(centralChunks);
  const endRecord = new Uint8Array(22);
  writeUint32(endRecord, 0, 0x06054b50);
  writeUint16(endRecord, 8, files.length);
  writeUint16(endRecord, 10, files.length);
  writeUint32(endRecord, 12, centralDirectory.length);
  writeUint32(endRecord, 16, offset);
  return concatBytes([...localChunks, centralDirectory, endRecord]);
}

function bytesToDataUrl(bytes, mimeType) {
  let binary = '';
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }
  return `data:${mimeType};base64,${window.btoa(binary)}`;
}

function downloadDataUrl(dataUrl, fileName) {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function paragraphXml(text, style = '') {
  const paragraphStyle = style ? `<w:pPr><w:pStyle w:val="${style}"/></w:pPr>` : '';
  return `<w:p>${paragraphStyle}<w:r><w:t xml:space="preserve">${escapeXml(text)}</w:t></w:r></w:p>`;
}

function imageDrawingXml({ relId, name, width = 1200, height = 800, index }) {
  const maxCx = 5486400;
  const cx = maxCx;
  const cy = Math.max(900000, Math.round(maxCx * (height / Math.max(width, 1))));
  return `
    <w:p>
      <w:r>
        <w:drawing>
          <wp:inline distT="0" distB="0" distL="0" distR="0">
            <wp:extent cx="${cx}" cy="${Math.min(cy, 4300000)}"/>
            <wp:docPr id="${index}" name="${escapeXml(name)}"/>
            <a:graphic>
              <a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/picture">
                <pic:pic>
                  <pic:nvPicPr><pic:cNvPr id="${index}" name="${escapeXml(name)}"/><pic:cNvPicPr/></pic:nvPicPr>
                  <pic:blipFill><a:blip r:embed="${relId}"/><a:stretch><a:fillRect/></a:stretch></pic:blipFill>
                  <pic:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="${cx}" cy="${Math.min(cy, 4300000)}"/></a:xfrm><a:prstGeom prst="rect"><a:avLst/></a:prstGeom></pic:spPr>
                </pic:pic>
              </a:graphicData>
            </a:graphic>
          </wp:inline>
        </w:drawing>
      </w:r>
    </w:p>`;
}

function buildReportDocx({ header, domain, charts, snapshots, comments, findings, executiveSummary }) {
  const imageFiles = [];
  const relationships = [];
  const body = [
    paragraphXml(header.title || 'Smarti report', 'Title'),
    paragraphXml(header.subtitle || ''),
    paragraphXml(`${domain === 'production' ? 'Test machine' : 'Burn-in'} report`),
    paragraphXml(`Prepared with ${artavaBrand.productName} by ${artavaBrand.companyName}`),
    paragraphXml(`Customer: ${header.customerName || '-'}`),
    paragraphXml(`Prepared by: ${header.preparedBy || '-'}`),
    paragraphXml(`Date: ${header.reportDate || '-'}`),
    paragraphXml(`Company website: ${artavaBrand.websiteHost}`),
  ];

  if (header.headerNote) body.push(paragraphXml(header.headerNote));
  if (executiveSummary) {
    body.push(paragraphXml('Executive summary', 'Heading1'));
    body.push(paragraphXml(executiveSummary));
  }

  charts.forEach((chart, chartIndex) => {
    const snapshot = snapshots[chart.key];
    const finding = findings[chart.key];
    body.push(paragraphXml(chart.label, 'Heading1'));
    body.push(paragraphXml(chart.description || ''));
    if (finding) {
      body.push(paragraphXml('Key finding', 'Heading2'));
      body.push(paragraphXml(finding));
    }
    if (snapshot?.imageDataUrl) {
      const relId = `rId${chartIndex + 1}`;
      const extension = snapshot.imageDataUrl.startsWith('data:image/jpeg') ? 'jpg' : 'png';
      const fileName = `image${chartIndex + 1}.${extension}`;
      imageFiles.push({ name: `word/media/${fileName}`, data: dataUrlToBytes(snapshot.imageDataUrl) });
      relationships.push(`<Relationship Id="${relId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="media/${fileName}"/>`);
      body.push(imageDrawingXml({
        relId,
        name: chart.label,
        width: snapshot.width || 1200,
        height: snapshot.height || 800,
        index: chartIndex + 1,
      }));
    }
    if (comments[chart.key]) {
      body.push(paragraphXml('Comment', 'Heading2'));
      body.push(paragraphXml(comments[chart.key]));
    }
  });

  const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
    <Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
      <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
      <Default Extension="xml" ContentType="application/xml"/>
      <Default Extension="png" ContentType="image/png"/>
      <Default Extension="jpg" ContentType="image/jpeg"/>
      <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
    </Types>`;
  const rootRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
    <Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
      <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
    </Relationships>`;
  const documentRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
    <Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${relationships.join('')}</Relationships>`;
  const documentXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
    <w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"
      xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"
      xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing"
      xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"
      xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture">
      <w:body>
        ${body.join('')}
        <w:sectPr><w:pgSz w:w="11906" w:h="16838"/><w:pgMar w:top="720" w:right="720" w:bottom="720" w:left="720"/></w:sectPr>
      </w:body>
    </w:document>`;

  const zipBytes = createZip([
    { name: '[Content_Types].xml', data: contentTypes },
    { name: '_rels/.rels', data: rootRels },
    { name: 'word/document.xml', data: documentXml },
    { name: 'word/_rels/document.xml.rels', data: documentRels },
    ...imageFiles,
  ]);
  return bytesToDataUrl(zipBytes, DOCX_MIME_TYPE);
}

function addCanvasAcrossPages(pdf, canvas, options) {
  const {
    margin,
    pageWidth,
    pageHeight,
    startY,
    imageFormat = 'PNG',
    imageMime = 'image/png',
    imageQuality,
  } = options;

  const maxWidth = pageWidth - (margin * 2);
  const maxHeight = pageHeight - (margin * 2);
  const widthScale = maxWidth / canvas.width;
  const heightScale = maxHeight / canvas.height;
  const scale = Math.min(widthScale, heightScale);
  const renderedWidth = canvas.width * scale;
  const renderedHeight = canvas.height * scale;

  if (startY + renderedHeight <= pageHeight - margin) {
    const x = (pageWidth - renderedWidth) / 2;
    pdf.addImage(canvas.toDataURL(imageMime, imageQuality), imageFormat, x, startY, renderedWidth, renderedHeight);
    return startY + renderedHeight;
  }

  pdf.addPage();
  const x = (pageWidth - renderedWidth) / 2;
  pdf.addImage(canvas.toDataURL(imageMime, imageQuality), imageFormat, x, margin, renderedWidth, renderedHeight);
  return margin + renderedHeight;
}

async function buildReportPdf(node, options = {}) {
  const {
    headerScale = 2,
    chartScale = 1.8,
    imageFormat = 'PNG',
    imageMime = 'image/png',
    imageQuality,
  } = options;
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 8;
  const blocks = [
    node.querySelector(`.${styles.reportHeader}`),
    ...Array.from(node.querySelectorAll(`.${styles.chartSection}`)),
  ].filter(Boolean);

  let currentY = margin;

  for (let index = 0; index < blocks.length; index += 1) {
    const block = blocks[index];
    const canvas = await renderNodeToCanvas(block, index === 0 ? headerScale : chartScale);
    currentY = addCanvasAcrossPages(pdf, canvas, {
      margin,
      pageWidth,
      pageHeight,
      startY: currentY,
      imageFormat,
      imageMime,
      imageQuality,
    });

    const nextBlockExists = index < blocks.length - 1;
    if (nextBlockExists) {
      const gap = 6;
      currentY += gap;
      if (currentY > pageHeight - margin - 40) {
        pdf.addPage();
        currentY = margin;
      }
    }
  }

  const reportTitle = node.querySelector(`.${styles.reportHeader} h2`)?.textContent || 'Smarti report';
  applyBrandedPdfChrome(pdf, {
    title: reportTitle,
    sourceLabel: artavaBrand.companyName,
  });

  return pdf;
}

async function buildMessagingReportPdf(node) {
  let smallestDataUrl = '';

  for (let index = 0; index < MESSAGING_REPORT_PDF_PROFILES.length; index += 1) {
    const pdf = await buildReportPdf(node, MESSAGING_REPORT_PDF_PROFILES[index]);
    const dataUrl = pdf.output('datauristring');
    if (!smallestDataUrl || dataUrl.length < smallestDataUrl.length) {
      smallestDataUrl = dataUrl;
    }
    if (dataUrl.length <= MESSAGING_REPORT_DATA_URL_LIMIT) {
      return {
        dataUrl,
        optimized: index > 0,
      };
    }
  }

  const megabytes = Math.max(0.1, (smallestDataUrl.length / 1024 / 1024).toFixed(1));
  throw new Error(`This report is still too large for messaging after optimization (${megabytes} MB). Remove a chart or shorten the report.`);
}

async function exportNodeToPdf(node, fileName) {
  const pdf = await buildReportPdf(node);
  pdf.save(fileName);
}

export default function ReportBuilderPage({ currentUser, entryMode = 'report-builder' }) {
  const { t } = useTranslation();
  const reportRef = useRef(null);
  const previewRef = useRef(null);
  const [domain, setDomain] = useState('production');
  const [selectedCharts, setSelectedCharts] = useState([]);
  const [header, setHeader] = useState({
    ...defaultHeader,
    preparedBy: currentUser?.fullName || currentUser?.username || '',
    customerName: currentUser?.company || '',
  });
  const [chartComments, setChartComments] = useState({});
  const [commentModal, setCommentModal] = useState(emptyCommentState);
  const [chartModal, setChartModal] = useState(null);
  const [chartLibraryOpen, setChartLibraryOpen] = useState(false);
  const [previewInstanceKey, setPreviewInstanceKey] = useState(0);
  const [chartSnapshots, setChartSnapshots] = useState({});
  const [chartFindings, setChartFindings] = useState({});
  const [executiveSummary, setExecutiveSummary] = useState('');
  const [compactPreview, setCompactPreview] = useState(false);
  const [savedReports, setSavedReports] = useState([]);
  const [loadingSavedReports, setLoadingSavedReports] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');
  const [savingReport, setSavingReport] = useState(false);
  const [reportSaveFormat, setReportSaveFormat] = useState('pdf');
  const [savedReportName, setSavedReportName] = useState(defaultHeader.title);
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const chartCatalog = useMemo(() => getChartCatalog(domain), [domain]);
  const selectedChartDefinitions = useMemo(
    () => chartCatalog.filter((chart) => selectedCharts.includes(chart.key)),
    [chartCatalog, selectedCharts]
  );
  const currentSaveExtension = reportSaveFormat === 'docx' ? 'docx' : 'pdf';
  const outputFileName = useMemo(
    () => buildReportFileName({
      domain,
      title: savedReportName || header.title,
      date: header.reportDate,
      extension: currentSaveExtension,
    }),
    [currentSaveExtension, domain, header.reportDate, header.title, savedReportName]
  );
  const exportPdfFileName = useMemo(
    () => buildReportFileName({
      domain,
      title: header.title,
      date: header.reportDate,
      extension: 'pdf',
    }),
    [domain, header.reportDate, header.title]
  );
  const exportDocxFileName = useMemo(
    () => buildReportFileName({
      domain,
      title: savedReportName || header.title,
      date: header.reportDate,
      extension: 'docx',
    }),
    [domain, header.reportDate, header.title, savedReportName]
  );
  const missingCapturedCharts = useMemo(
    () => selectedChartDefinitions.filter((chart) => !chartSnapshots[chart.key]?.imageDataUrl),
    [chartSnapshots, selectedChartDefinitions]
  );

  const loadSavedReports = useCallback(async () => {
    setLoadingSavedReports(true);
    try {
      const result = await fetchSavedReports();
      setSavedReports(Array.isArray(result.reports) ? result.reports.slice(0, 5) : []);
    } catch (savedReportError) {
      console.warn('Saved reports could not be loaded:', savedReportError);
    } finally {
      setLoadingSavedReports(false);
    }
  }, []);

  useEffect(() => {
    loadSavedReports();
  }, [loadSavedReports]);

  useEffect(() => {
    setSavedReportName((current) => {
      const trimmed = String(current || '').trim();
      if (!trimmed || trimmed === 'Smarti report') {
        return header.title || defaultHeader.title;
      }
      return current;
    });
  }, [header.title]);

  const handleHeaderChange = (key, value) => {
    setHeader((current) => ({ ...current, [key]: value }));
  };

  const handleLogoPicked = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      handleHeaderChange('logoDataUrl', String(reader.result || ''));
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const openChartModal = (chartDefinition) => {
    setChartModal(chartDefinition);
    setPreviewInstanceKey((current) => current + 1);
    setError('');
    const visitPath = buildReportChartVisitPath(domain, chartDefinition);
    sendAuthPageVisit(visitPath, getReportBuilderClientMeta(visitPath)).catch((error) => {
      console.warn('Report chart visit tracking failed:', error);
    });
  };

  const handleChartSelection = (chartDefinition, checked) => {
    setSelectedCharts((current) => (
      checked
        ? current.includes(chartDefinition.key)
          ? current
          : [...current, chartDefinition.key]
        : current.filter((key) => key !== chartDefinition.key)
    ));

    if (checked) {
      openChartModal(chartDefinition);
    }
  };

  const captureChartSnapshot = async () => {
    if (!previewRef.current || !chartModal) return;
    setCapturing(true);
    setError('');
    try {
      const captureTarget = resolveCaptureNode(previewRef.current, chartModal);
      const canvas = await html2canvas(captureTarget || previewRef.current, {
        backgroundColor: '#ffffff',
        scale: 1.8,
        useCORS: true,
        scrollY: -window.scrollY,
      });
      const imageDataUrl = canvas.toDataURL('image/png');
      setChartSnapshots((current) => ({
        ...current,
        [chartModal.key]: {
          title: chartModal.label,
          imageDataUrl,
          capturedAt: new Date().toISOString(),
          width: canvas.width,
          height: canvas.height,
        },
      }));
      setChartModal(null);
    } catch (captureError) {
      setError(captureError.message || 'Could not capture the configured chart preview.');
    } finally {
      setCapturing(false);
    }
  };

  const saveComment = () => {
    setChartComments((current) => ({ ...current, [commentModal.chartKey]: commentModal.text }));
    setCommentModal(emptyCommentState);
  };

  const handleExecutiveSummaryAutofill = () => {
    setExecutiveSummary(buildExecutiveSummary({
      domain,
      header,
      selectedCharts: selectedChartDefinitions,
      chartFindings,
      chartComments,
      chartSnapshots,
    }));
  };

  const exportPdf = async () => {
    if (!reportRef.current) return;
    setExporting(true);
    setExportFormat('pdf');
    setError('');
    setSuccessMessage('');
    try {
      await exportNodeToPdf(reportRef.current, exportPdfFileName);
    } catch (exportError) {
      setError(exportError.message || 'Could not export the PDF.');
    } finally {
      setExporting(false);
    }
  };

  const exportDocx = async () => {
    if (!selectedChartDefinitions.length || !allSelectedChartsCaptured) return;
    setExporting(true);
    setExportFormat('docx');
    setError('');
    setSuccessMessage('');
    try {
      const dataUrl = buildReportDocx({
        header,
        domain,
        charts: selectedChartDefinitions,
        snapshots: chartSnapshots,
        comments: chartComments,
        findings: chartFindings,
        executiveSummary,
      });
      downloadDataUrl(dataUrl, exportDocxFileName);
    } catch (exportError) {
      setError(exportError.message || 'Could not export the DOCX.');
    } finally {
      setExporting(false);
    }
  };

  const saveReportForMessaging = async () => {
    if (!reportRef.current) return;
    setSavingReport(true);
    setError('');
    setSuccessMessage('');
    try {
      const isDocx = reportSaveFormat === 'docx';
      const reportFile = isDocx
        ? {
            dataUrl: buildReportDocx({
              header,
              domain,
              charts: selectedChartDefinitions,
              snapshots: chartSnapshots,
              comments: chartComments,
              findings: chartFindings,
              executiveSummary,
            }),
            mimeType: DOCX_MIME_TYPE,
            optimized: false,
          }
        : {
            ...(await buildMessagingReportPdf(reportRef.current)),
            mimeType: 'application/pdf',
          };
      const result = await saveBuiltReport({
        title: String(savedReportName || header.title || 'Smarti report').trim() || 'Smarti report',
        fileName: outputFileName,
        mimeType: reportFile.mimeType,
        dataUrl: reportFile.dataUrl,
      });
      setSuccessMessage(reportFile.optimized
        ? 'Report was adjusted to fit messaging and saved. You can now attach it from Smarti messaging.'
        : (result.message || `${currentSaveExtension.toUpperCase()} report saved. You can now attach it from Smarti messaging.`));
    } catch (saveError) {
      setError(saveError.message || 'Could not save the report for messaging.');
    } finally {
      setSavingReport(false);
    }
  };

  const allSelectedChartsCaptured = selectedChartDefinitions.every((chart) => chartSnapshots[chart.key]?.imageDataUrl);
  const selectedCount = selectedChartDefinitions.length;
  const capturedCount = selectedChartDefinitions.filter((chart) => chartSnapshots[chart.key]?.imageDataUrl).length;
  const isOperationsEntry = entryMode === 'operations';

  return (
    <div className={styles.container}>
      <GoToDashboardButton />
      <div className={styles.content}>
        <header className={styles.hero}>
          <div>
            <p className={styles.eyebrow}>{t(isOperationsEntry ? 'Make report' : 'Custom reporting')}</p>
            <h1 className={styles.title}>{t(isOperationsEntry ? 'Make report' : 'Report builder')}</h1>
            <p className={styles.description}>
              {t('Choose the data source, add the exact charts you want, open each chart in a live modal with its real filters, capture only the chart itself, then export a clean PDF.')}
            </p>
            <div className={styles.progressStrip}>
              <div className={styles.progressPill}>
                <span>{t('Source')}</span>
                <strong>{domain === 'production' ? t('Test machine') : t('Burn-in')}</strong>
              </div>
              <div className={styles.progressPill}>
                <span>{t('Charts selected')}</span>
                <strong>{selectedCount}</strong>
              </div>
              <div className={styles.progressPill}>
                <span>{t('Charts captured')}</span>
                <strong>{capturedCount}</strong>
              </div>
            </div>
          </div>
          <div className={styles.heroAside}>
            <div className={styles.nextStepCard}>
              <span>{t('What to do next')}</span>
              <strong>
                {selectedCount === 0
                  ? t('Choose a source and pick your first chart.')
                  : capturedCount < selectedCount
                    ? t('Open each selected chart and capture the configured chart.')
                    : t('Review comments and export the PDF.')}
              </strong>
              <p>
                {selectedCount === 0
                  ? t('Start on the left with test machine or burn-in, then tick a chart to open its live modal.')
                  : capturedCount < selectedCount
                    ? t('The report only becomes export-ready after every selected chart has a captured chart image.')
                    : t('Your report is ready. Re-open any chart if you want a different filter combination before export.')}
              </p>
            </div>
            <button type="button" className={styles.secondaryButton} onClick={() => {
              setSelectedCharts([]);
              setChartSnapshots({});
              setChartFindings({});
              setChartComments({});
              setExecutiveSummary('');
              setChartModal(null);
              setCommentModal(emptyCommentState);
              setError('');
            }}>
              {t('Clear report')}
            </button>
            <button type="button" className={styles.primaryButton} onClick={exportPdf} disabled={exporting || !selectedChartDefinitions.length || !allSelectedChartsCaptured}>
              {exporting && exportFormat === 'pdf' ? t('Exporting PDF...') : t('Export PDF')}
            </button>
            <button type="button" className={styles.secondaryButton} onClick={exportDocx} disabled={exporting || !selectedChartDefinitions.length || !allSelectedChartsCaptured}>
              {exporting && exportFormat === 'docx' ? t('Exporting DOCX...') : t('Export DOCX')}
            </button>
            <div className={styles.reportFormatSelect}>
              <span>{t('Messaging format')}</span>
              <div className={styles.formatChipRow}>
                {['pdf', 'docx'].map((format) => (
                  <button
                    key={format}
                    type="button"
                    className={`${styles.formatChip} ${reportSaveFormat === format ? styles.formatChipActive : ''}`}
                    onClick={() => setReportSaveFormat(format)}
                  >
                    {format.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <button type="button" className={styles.primaryButton} onClick={saveReportForMessaging} disabled={savingReport || !selectedChartDefinitions.length || !allSelectedChartsCaptured}>
              {savingReport ? t('Saving report...') : t('Save for messaging')}
            </button>
          </div>
        </header>

        {error ? <div className={`${styles.noticeCard} ${styles.errorNotice}`}>{error}</div> : null}
        {successMessage ? <div className={`${styles.noticeCard} ${styles.successNotice}`}>{successMessage}</div> : null}
        {!allSelectedChartsCaptured && selectedChartDefinitions.length ? (
          <div className={`${styles.noticeCard} ${styles.warningNotice}`}>
            <strong>{t('Missing capture warning')}</strong>
            <p>{t('Open each missing chart, tune the real filters, then capture it before exporting or saving the report.')}</p>
            <div className={styles.selectionTokenWrap}>
              {missingCapturedCharts.map((chart) => (
                <span key={chart.key} className={styles.warningToken}>{chart.label}</span>
              ))}
            </div>
          </div>
        ) : null}

        <section className={styles.builderGrid}>
          <aside className={styles.controlPanel}>
            <div className={styles.panelCard}>
              <h2>{t('1. Choose data source')}</h2>
              <div className={styles.domainToggle}>
                {REPORT_DOMAINS.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`${styles.domainButton} ${domain === item.key ? styles.domainButtonActive : ''}`}
                    onClick={() => {
                      setDomain(item.key);
                      setSelectedCharts([]);
                      setChartSnapshots({});
                      setChartFindings({});
                      setChartComments({});
                      setExecutiveSummary('');
                      setChartModal(null);
                      setCommentModal(emptyCommentState);
                      setError('');
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.panelCard}>
              <h2>{t('2. Choose from all available charts')}</h2>
              <p className={styles.panelLead}>{t('Keep the page short here, then open the chart library only when you want to add or configure figures.')}</p>
              <div className={styles.selectionSummaryCard}>
                <div className={styles.selectionSummaryRow}>
                  <span>{t('Available in this source')}</span>
                  <strong>{chartCatalog.length}</strong>
                </div>
                <div className={styles.selectionSummaryRow}>
                  <span>{t('Currently selected')}</span>
                  <strong>{selectedCount}</strong>
                </div>
                <div className={styles.selectionSummaryRow}>
                  <span>{t('Already captured')}</span>
                  <strong>{capturedCount}</strong>
                </div>
                {selectedChartDefinitions.length ? (
                  <div className={styles.selectionTokenWrap}>
                    {selectedChartDefinitions.slice(0, 6).map((chart) => (
                      <span key={chart.key} className={styles.selectionToken}>{chart.label}</span>
                    ))}
                    {selectedChartDefinitions.length > 6 ? (
                      <span className={styles.selectionToken}>+{selectedChartDefinitions.length - 6}</span>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div className={styles.libraryActionRow}>
                <button type="button" className={styles.primaryButton} onClick={() => setChartLibraryOpen(true)}>
                  {t('Open chart library')}
                </button>
                {selectedChartDefinitions.length ? (
                  <button type="button" className={styles.secondaryButton} onClick={() => setChartLibraryOpen(true)}>
                    {t('Manage selected charts')}
                  </button>
                ) : null}
              </div>
            </div>

            <div className={styles.panelCard}>
              <h2>{t('3. Customize header')}</h2>
              <div className={styles.formGrid}>
                <label className={styles.field}>
                  <span>{t('Report title')}</span>
                  <input value={header.title} onChange={(event) => handleHeaderChange('title', event.target.value)} />
                </label>
                <label className={styles.field}>
                  <span>{t('Subtitle')}</span>
                  <input value={header.subtitle} onChange={(event) => handleHeaderChange('subtitle', event.target.value)} />
                </label>
                <label className={styles.field}>
                  <span>{t('Customer')}</span>
                  <input value={header.customerName} onChange={(event) => handleHeaderChange('customerName', event.target.value)} />
                </label>
                <label className={styles.field}>
                  <span>{t('Prepared by')}</span>
                  <input value={header.preparedBy} onChange={(event) => handleHeaderChange('preparedBy', event.target.value)} />
                </label>
                <label className={styles.field}>
                  <span>{t('Report date')}</span>
                  <input type="date" value={header.reportDate} onChange={(event) => handleHeaderChange('reportDate', event.target.value)} />
                </label>
                <label className={styles.field}>
                  <span>{t('Saved report name')}</span>
                  <input value={savedReportName} onChange={(event) => setSavedReportName(event.target.value)} placeholder={t('Choose how this report appears in messaging.')} />
                </label>
                <label className={styles.field}>
                  <span>{t('Auto filename')}</span>
                  <input value={outputFileName} readOnly />
                </label>
                <label className={`${styles.field} ${styles.fieldWide}`}>
                  <span>{t('Header note')}</span>
                  <textarea rows={4} value={header.headerNote} onChange={(event) => handleHeaderChange('headerNote', event.target.value)} placeholder={t('Write the context, scope, or message you want at the top of the report.')} />
                </label>
                <label className={`${styles.field} ${styles.fieldWide}`}>
                  <span>{t('Executive summary')}</span>
                  <textarea rows={5} value={executiveSummary} onChange={(event) => setExecutiveSummary(event.target.value)} placeholder={t('Summarize the overall story of the report for a manager or customer.')} />
                </label>
                <div className={`${styles.field} ${styles.fieldWide}`}>
                  <span>{t('Quick actions')}</span>
                  <div className={styles.inlineActionRow}>
                    <button type="button" className={styles.secondaryButton} onClick={handleExecutiveSummaryAutofill}>
                      {t('Add executive summary')}
                    </button>
                    <button
                      type="button"
                      className={`${styles.secondaryButton} ${compactPreview ? styles.toggleButtonActive : ''}`}
                      onClick={() => setCompactPreview((current) => !current)}
                    >
                      {compactPreview ? t('Preview compact on') : t('Preview compact mode')}
                    </button>
                  </div>
                </div>
                <label className={`${styles.field} ${styles.fieldWide}`}>
                  <span>{t('Logo')}</span>
                  <div className={styles.logoActions}>
                    <label className={styles.secondaryButton}>
                      {t('Upload logo')}
                      <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleLogoPicked} hidden />
                    </label>
                    {header.logoDataUrl ? <button type="button" className={styles.secondaryButton} onClick={() => handleHeaderChange('logoDataUrl', '')}>{t('Remove logo')}</button> : null}
                  </div>
                </label>
              </div>
            </div>

            <div className={styles.panelCard}>
              <div className={styles.panelTitleRow}>
                <h2>{t('4. Last saved reports')}</h2>
                <button type="button" className={styles.secondaryButton} onClick={loadSavedReports} disabled={loadingSavedReports}>
                  {loadingSavedReports ? t('Refreshing...') : t('Refresh')}
                </button>
              </div>
              <div className={styles.savedReportList}>
                {savedReports.length ? savedReports.slice(0, 5).map((report) => (
                  <article key={report.id} className={styles.savedReportCard}>
                    <div className={styles.savedReportTopRow}>
                      <strong>{getSavedReportDisplayName(report)}</strong>
                      <span className={styles.savedReportType}>{formatMimeLabel(report.mimeType)}</span>
                    </div>
                    <p>{report.fileName}</p>
                    <span>{formatSavedReportDate(report.createdAt)}</span>
                  </article>
                )) : (
                  <div className={styles.savedReportEmpty}>
                    {loadingSavedReports ? t('Loading saved reports...') : t('Saved reports will appear here after you store one for messaging.')}
                  </div>
                )}
              </div>
            </div>
          </aside>

          <section className={styles.previewPanel}>
            {!selectedChartDefinitions.length ? (
              <div className={styles.emptyState}>
                <strong>{t('No charts selected yet')}</strong>
                <p>{t('Select any chart from the left. As soon as you select it, Smarti opens the real page in a modal so you can use the real filters before capturing it into the report.')}</p>
              </div>
            ) : (
              <div className={`${styles.reportCanvas} ${compactPreview ? styles.reportCanvasCompact : ''}`} ref={reportRef}>
                <header className={styles.reportHeader}>
                  <div className={styles.reportTopBand}>
                    <span>{t('Smarti Analytics Report')} • {artavaBrand.companyName}</span>
                    <strong>{domain === 'production' ? t('Test machine') : t('Burn-in')}</strong>
                  </div>
                  <div className={styles.reportHeroBlock}>
                    <div className={styles.reportHeaderMain}>
                      {header.logoDataUrl ? <img src={header.logoDataUrl} alt="Report logo" className={styles.reportLogo} /> : null}
                      <div>
                        <p className={styles.reportEyebrow}>{domain === 'production' ? t('Test machine report') : t('Burn-in report')}</p>
                        <h2>{header.title || t('Smarti report')}</h2>
                        <p>{header.subtitle}</p>
                      </div>
                    </div>
                    <div className={styles.reportSummaryGrid}>
                      <div className={styles.reportSummaryCard}>
                        <span>{t('Customer')}</span>
                        <strong>{header.customerName || '—'}</strong>
                      </div>
                      <div className={styles.reportSummaryCard}>
                        <span>{t('Prepared by')}</span>
                        <strong>{header.preparedBy || '—'}</strong>
                      </div>
                      <div className={styles.reportSummaryCard}>
                        <span>{t('Date')}</span>
                        <strong>{header.reportDate || '—'}</strong>
                      </div>
                      <div className={styles.reportSummaryCard}>
                        <span>{t('Charts included')}</span>
                        <strong>{selectedChartDefinitions.length}</strong>
                      </div>
                    </div>
                  </div>
                  {header.headerNote && !compactPreview ? <div className={styles.headerNote}>{header.headerNote}</div> : null}
                  {executiveSummary ? (
                    <div className={styles.executiveSummaryCard}>
                      <span>{t('Executive summary')}</span>
                      <p>{executiveSummary}</p>
                    </div>
                  ) : null}
                </header>

                <div className={styles.chartSectionList}>
                  {selectedChartDefinitions.map((chart) => {
                    const snapshot = chartSnapshots[chart.key];
                    return (
                      <article key={chart.key} className={styles.chartSection}>
                        <div className={styles.chartSectionHeader}>
                          <div>
                            <span className={styles.sectionBadge}>{chart.label}</span>
                            <h3>{chart.label}</h3>
                            {!compactPreview ? <p>{chart.description}</p> : null}
                          </div>
                          <div className={styles.sectionActionStack}>
                            <button type="button" className={styles.secondaryButton} onClick={() => openChartModal(chart)}>
                              {snapshot ? t('Re-open and update') : t('Open and configure')}
                            </button>
                            <button
                              type="button"
                              className={styles.secondaryButton}
                              onClick={() => setCommentModal({ chartKey: chart.key, text: chartComments[chart.key] || '' })}
                              disabled={!snapshot}
                            >
                              {chartComments[chart.key] ? t('Edit comment') : t('Add comment')}
                            </button>
                          </div>
                        </div>

                        {snapshot?.imageDataUrl ? (
                          <div className={styles.capturedImageCard}>
                            <div className={styles.figureMetaRow}>
                              <span>{t('Figure')}</span>
                              <strong>{chart.label}</strong>
                            </div>
                            <img src={snapshot.imageDataUrl} alt={chart.label} className={styles.capturedImage} />
                          </div>
                        ) : (
                          <div className={`${styles.chartFallback} ${styles.warningBlock}`}>
                            {t('This chart is selected but has not been captured yet. Open it, adjust the real filters in the modal, then capture the current result into the report.')}
                          </div>
                        )}

                        <label className={`${styles.field} ${styles.fieldWide}`}>
                          <span>{t('Key finding')}</span>
                          <textarea
                            rows={3}
                            value={chartFindings[chart.key] || ''}
                            onChange={(event) => setChartFindings((current) => ({ ...current, [chart.key]: event.target.value }))}
                            placeholder={t('Write the most important result from this chart in one or two sentences.')}
                          />
                        </label>

                        {chartFindings[chart.key] ? (
                          <div className={styles.findingCard}>
                            <strong>{t('Finding')}</strong>
                            <p>{chartFindings[chart.key]}</p>
                          </div>
                        ) : null}

                        {chartComments[chart.key] && !compactPreview ? (
                          <div className={styles.chartComment}>
                            <strong>{t('Comment after this chart')}</strong>
                            <p>{chartComments[chart.key]}</p>
                          </div>
                        ) : null}
                      </article>
                    );
                  })}
                </div>
                <footer className={styles.reportBrandFooter}>
                  <div>
                    <strong>{artavaBrand.companyName}</strong>
                    <span>{artavaBrand.shortDescription}</span>
                  </div>
                  <div>
                    <a href={artavaBrand.websiteUrl} target="_blank" rel="noreferrer">{artavaBrand.websiteHost}</a>
                    <a href={`mailto:${artavaBrand.contactEmail}`}>{artavaBrand.contactEmail}</a>
                  </div>
                </footer>
              </div>
            )}
          </section>
        </section>
      </div>

      <Modal
        isOpen={chartLibraryOpen}
        onRequestClose={() => setChartLibraryOpen(false)}
        className={styles.chartLibraryModal}
        overlayClassName={styles.modalOverlay}
        contentLabel={t('Chart library')}
      >
        <div className={styles.modalHeader}>
          <div>
            <p className={styles.eyebrow}>{t('Chart library')}</p>
            <h3>{t('Choose report charts')}</h3>
            <p className={styles.modalLead}>{t('Select any chart you want in the report. When you tick one, Smarti immediately opens its live configuration modal so you can apply the real filters and capture it.')}</p>
          </div>
          <div className={styles.modalActions}>
            <button type="button" className={styles.secondaryButton} onClick={() => setChartLibraryOpen(false)}>
              {t('Close')}
            </button>
          </div>
        </div>

        <div className={styles.chartLibraryBody}>
          <div className={styles.chartChoiceList}>
            {chartCatalog.map((chart) => {
              const isSelected = selectedCharts.includes(chart.key);
              const isCaptured = Boolean(chartSnapshots[chart.key]?.imageDataUrl);
              return (
                <div key={chart.key} className={styles.chartChoice}>
                  <label className={styles.chartChoiceMain}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(event) => handleChartSelection(chart, event.target.checked)}
                    />
                    <div>
                      <strong>{chart.label}</strong>
                      <p>{chart.description}</p>
                    </div>
                  </label>
                  <div className={styles.chartChoiceActions}>
                    <span className={`${styles.statusBadge} ${isCaptured ? styles.statusReady : styles.statusPending}`}>
                      {isCaptured ? t('Captured') : t('Not captured')}
                    </span>
                    <button type="button" className={styles.secondaryButton} onClick={() => openChartModal(chart)}>
                      {t('Open and configure')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={Boolean(chartModal)}
        onRequestClose={() => setChartModal(null)}
        className={styles.chartModal}
        overlayClassName={styles.modalOverlay}
        contentLabel={t('Configure chart for report')}
      >
        <div className={styles.modalHeader}>
          <div>
            <p className={styles.eyebrow}>{t('Live chart modal')}</p>
            <h3>{chartModal?.label}</h3>
            <p className={styles.modalLead}>{t('Use the exact filters and controls from the real chart page below. When it looks right, Smarti captures only the chart area, not the whole page.')}</p>
          </div>
          <div className={styles.modalActions}>
            <button type="button" className={styles.secondaryButton} onClick={() => setPreviewInstanceKey((current) => current + 1)}>
              {t('Reset modal')}
            </button>
            <button type="button" className={styles.secondaryButton} onClick={() => setChartModal(null)}>
              {t('Close')}
            </button>
            <button type="button" className={styles.primaryButton} onClick={captureChartSnapshot} disabled={capturing}>
              {capturing ? t('Capturing...') : t('Capture current chart into report')}
            </button>
          </div>
        </div>

        <div className={styles.previewSurface}>
          <div className={styles.previewInner} ref={previewRef}>
            {chartModal ? <ChartModalRenderer chartDefinition={chartModal} instanceKey={previewInstanceKey} /> : null}
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={Boolean(commentModal.chartKey)}
        onRequestClose={() => setCommentModal(emptyCommentState)}
        className={styles.commentModal}
        overlayClassName={styles.modalOverlay}
        contentLabel={t('Chart comment')}
      >
        <div className={styles.modalHeader}>
          <div>
            <p className={styles.eyebrow}>{t('Section comment')}</p>
            <h3>{t('Add text after the selected chart')}</h3>
          </div>
          <button type="button" className={styles.secondaryButton} onClick={() => setCommentModal(emptyCommentState)}>
            {t('Close')}
          </button>
        </div>
        <label className={styles.field}>
          <span>{t('Comment')}</span>
          <textarea rows={8} value={commentModal.text} onChange={(event) => setCommentModal((current) => ({ ...current, text: event.target.value }))} placeholder={t('Write what the reader should understand after this chart: cause, warning, interpretation, or action.')} />
        </label>
        <div className={styles.modalActions}>
          <button type="button" className={styles.primaryButton} onClick={saveComment}>{t('Save comment')}</button>
        </div>
      </Modal>
    </div>
  );
}

function ChartModalRenderer({ chartDefinition, instanceKey }) {
  const Component = chartDefinition?.Component;
  if (!Component) return null;
  return <Component key={`${chartDefinition.key}-${instanceKey}`} {...(chartDefinition.componentProps || {})} />;
}
